import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';

import { gqlClient } from '@/api';

/**
 * The second factor, as Settings › Security drives it: read where it
 * stands, enrol in two steps, turn it off, replace the recovery codes.
 *
 * Nothing here ever holds the secret past the enrolment modal: `beginSetup`
 * returns it once, for the QR and the text beside it, and the server keeps
 * it sealed from then on.
 */

export interface TwoFactorStatus {
  enabled: boolean;
  enabledAt: string | null;
  pending: boolean;
  recoveryCodesLeft: number;
  trustedDevices: number;
  storageUnavailable: boolean;
}

// A backtick literal on purpose: the admin's contract spec splices `${…}`
// field lists back into their documents by matching this exact shape.
const STATUS_FIELDS = `
  enabled enabledAt pending recoveryCodesLeft trustedDevices storageUnavailable
`;
const MY_TWO_FACTOR = gql`query MyTwoFactor { myTwoFactor { ${STATUS_FIELDS} } }`;
const BEGIN = gql`
  mutation BeginTwoFactorSetup {
    beginTwoFactorSetup {
      secret
      otpauthUrl
    }
  }
`;
const CONFIRM = gql`
  mutation ConfirmTwoFactorSetup($input: TwoFactorCodeInput!) {
    confirmTwoFactorSetup(input: $input) {
      recoveryCodes
    }
  }
`;
const CANCEL = gql`
  mutation CancelTwoFactorSetup {
    cancelTwoFactorSetup
  }
`;
const DISABLE = gql`
  mutation DisableTwoFactor($input: DisableTwoFactorInput!) {
    disableTwoFactor(input: $input)
  }
`;
const REGENERATE = gql`
  mutation RegenerateRecoveryCodes($input: TwoFactorCodeInput!) {
    regenerateRecoveryCodes(input: $input) {
      recoveryCodes
    }
  }
`;
const FORGET_DEVICES = gql`
  mutation ForgetTrustedDevices {
    forgetTrustedDevices
  }
`;

export const useMyTwoFactor = () =>
  useQuery({
    queryKey: ['myTwoFactor'],
    queryFn: async () =>
      (await gqlClient.request<{ myTwoFactor: TwoFactorStatus }>(MY_TWO_FACTOR)).myTwoFactor,
  });

const refresh = (queryClient: ReturnType<typeof useQueryClient>) =>
  void queryClient.invalidateQueries({ queryKey: ['myTwoFactor'] });

export const useBeginTwoFactorSetup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (
        await gqlClient.request<{ beginTwoFactorSetup: { secret: string; otpauthUrl: string } }>(
          BEGIN,
        )
      ).beginTwoFactorSetup,
    onSuccess: () => refresh(queryClient),
  });
};

export const useConfirmTwoFactorSetup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) =>
      (
        await gqlClient.request<{ confirmTwoFactorSetup: { recoveryCodes: string[] } }>(CONFIRM, {
          input: { code },
        })
      ).confirmTwoFactorSetup,
    onSuccess: () => refresh(queryClient),
  });
};

export const useCancelTwoFactorSetup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await gqlClient.request<{ cancelTwoFactorSetup: boolean }>(CANCEL)).cancelTwoFactorSetup,
    onSuccess: () => refresh(queryClient),
  });
};

export const useDisableTwoFactor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { password: string; code: string }) =>
      (await gqlClient.request<{ disableTwoFactor: boolean }>(DISABLE, { input })).disableTwoFactor,
    onSuccess: () => refresh(queryClient),
  });
};

export const useRegenerateRecoveryCodes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) =>
      (
        await gqlClient.request<{ regenerateRecoveryCodes: { recoveryCodes: string[] } }>(
          REGENERATE,
          {
            input: { code },
          },
        )
      ).regenerateRecoveryCodes,
    onSuccess: () => refresh(queryClient),
  });
};

export const useForgetTrustedDevices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await gqlClient.request<{ forgetTrustedDevices: number }>(FORGET_DEVICES))
        .forgetTrustedDevices,
    onSuccess: () => refresh(queryClient),
  });
};

/** What a refused code or password should say — the server's own words when it sent any. */
export const twoFactorMessage = (error: unknown): string => {
  const first = (error as { response?: { errors?: { message?: string }[] } })?.response?.errors?.[0]
    ?.message;
  return first ?? 'Could not reach the server. Nothing was changed.';
};
